package ui

import io.kvision.core.*
import io.kvision.html.CustomTag
import io.kvision.html.Image
import io.kvision.html.customTag
import io.kvision.html.image
import io.kvision.panel.SimplePanel
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.coroutines.await
import models.IconSize
import models.TerritoryState
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.File
import org.w3c.files.FileReader
import structs.MapData
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import structs.MapPackManager
import structs.Territory
import structs.UnpackedMapPack
import kotlin.coroutines.resume
import kotlin.math.pow
import kotlin.math.sqrt

/**
 * Interactive map viewer widget that displays territories with clickable icons.
 */
class MapViewer : SimplePanel()
{
    
    private var backgroundImage: Image? = null
    private val territoryIcons = mutableMapOf<String, TerritoryIcon>()
    private var mapData: MapData? = null
    var isMapLoaded = false

    /**
     * Inner canvas panel that owns the pan/zoom transform. All map
     * children (background image, SVG border layer, TerritoryIcons)
     * live under [mapCanvas] instead of directly under this MapViewer
     * so a single `transform: translate(tx,ty) scale(z)` on this panel
     * pans/zooms the entire map surface in lockstep.
     *
     * MapViewer itself stays the clipping container (`overflow: hidden`),
     * the mapCanvas is what pannable transforms apply to.
     */
    private var mapCanvas: SimplePanel? = null

    /**
     * Pan/zoom state. All transforms compose on mapCanvas.style.transform
     * via [applyTransform]. Read via the public getters below for the
     * Playwright e2e harness.
     */
    private var panX: Double = 0.0
    private var panY: Double = 0.0
    private var zoom: Double = 1.0

    /** Bounds for pinch-zoom and CSS-driven scroll-zoom. */
    private val minZoom = 0.5
    private val maxZoom = 3.0

    /**
     * True when the device is touch-capable or the viewport is narrow
     * enough to be a phone. Pointer event handlers for drag-pan and
     * pinch-zoom are gated on this; desktop keeps the existing
     * hover-and-click interaction for territory icons.
     */
    val isMobile: Boolean
        get() = kotlinx.browser.window.matchMedia("(pointer: coarse)").matches ||
                kotlinx.browser.window.innerWidth < 768

    /** Read-only pan/zoom state for e2e introspection. */
    val currentPanX: Double get() = panX
    val currentPanY: Double get() = panY
    val currentZoom: Double get() = zoom

    /**
     * Name of the first pin in the loaded map, or null if the map isn't
     * loaded yet. Exposed so the Playwright e2e harness can call
     * centerOnTerritory without having to walk the (minified)
     * mapData.pins array.
     */
    val firstTerritoryName: String?
        get() = mapData?.pins?.firstOrNull()?.territory?.name

    var onTerritoryClick: ((Territory) -> Unit)? = null
    var onTerritoryHover: ((Territory) -> Unit)? = null

    private var updateOwnershipCallCount = 0
    private var totalUpdateOwnershipDuration = 0.0

    /**
     * SVG overlay that hosts the per-hover border lines. Sits above
     * the background image (zIndex 1) and below the territory icons
     * (zIndex 10). `pointer-events: none` so the icons remain the
     * hover target; the lines are purely visual.
     *
     * Initialized once in [init] and reused for the lifetime of the
     * widget. Children are added/removed via [svgLineGroup] (the
     * inner `<g>` element); the outer `<svg>` is never replaced, so
     * the snabbdom diff for hover changes is scoped to the inner
     * group and never re-renders the background image or the icon
     * tree.
     */
    private var svgLayer: CustomTag? = null

    /**
     * The inner `<g>` group whose children are the `<line>` elements
     * for the currently-hovered territory. Cleared on every
     * hover-out and rebuilt on every hover-in. Keeping the lines
     * inside a dedicated group means `removeAll()` only diffs that
     * group, not the SVG element itself.
     */
    private var svgLineGroup: CustomTag? = null

    /**
     * Pure-logic helper that turns the current [mapData] into
     * per-hover line geometry. Rebuilt every time a new map pack is
     * loaded; the [loadMapPack] / [clearMap] pair owns the lifecycle.
     */
    private var borderRenderer: HoverBorderLineRenderer? = null

    init
    {
        addCssClass("map-viewer-container")
        width = CssSize(100, UNIT.perc)
        height = CssSize(100, UNIT.vh)
        position = Position.RELATIVE
        overflow = Overflow.HIDDEN

        // The inner canvas is the pan/zoom surface. All map children
        // (background image, SVG border layer, TerritoryIcons) live
        // under mapCanvas so a single transform on this panel pans/zooms
        // the entire map in lockstep. See clampToBounds + applyTransform
        // for the math; see wirePanHandlers + wirePinchHandlers for the
        // mobile-only pointer event wiring.
        mapCanvas = SimplePanel().apply {
            addCssClass("map-canvas")
            width = CssSize(100, UNIT.perc)
            height = CssSize(100, UNIT.perc)
            position = Position.ABSOLUTE
            left = CssSize(0, UNIT.px)
            top = CssSize(0, UNIT.px)
            setStyle("transform-origin", "0 0")
            setStyle("will-change", "transform")
        }
        add(mapCanvas!!)

        // Background image layer
        backgroundImage = image(src = "", alt = "Map") {
            position = Position.ABSOLUTE
            left = CssSize(0, UNIT.px)
            top = CssSize(0, UNIT.px)
            width = CssSize(100, UNIT.perc)
            height = CssSize(100, UNIT.perc)
            zIndex = 1
            setStyle("object-fit", "fill")
            setStyle("pointer-events", "none")
        }
        mapCanvas!!.add(backgroundImage!!)

        // SVG border-line overlay. Stays mounted for the lifetime
        // of the widget; only its inner <g> child list mutates per
        // hover. Sized 100% / 100% so the percent-attribute lines
        // track the background image as the container resizes.
        svgLayer = customTag("svg") {
            setAttribute("width", "100%")
            setAttribute("height", "100%")
            setAttribute("data-testid", "map-svg-borders")
            setAttribute("style", "position: absolute; top: 0; left: 0; pointer-events: none; z-index: 5;")
            svgLineGroup = customTag("g")
        }
        mapCanvas!!.add(svgLayer!!)

        // Wire pointer events for mobile drag-pan + pinch-zoom. Bails
        // early on desktop so existing hover/click interaction on the
        // TerritoryIcon children is preserved.
        wirePanHandlers()
        wirePinchHandlers()
    }
    
    /**
     * Loads a map pack and displays territories with icons.
     *
     * @param packBytes The map pack data as ByteArray.
     */
    suspend fun loadMapPack(packBytes: ByteArray)
    {
        val totalStart = kotlinx.browser.window.performance.now()
        Logger.debug(LogCategory.UI, "MapViewer: Loading map pack...")
        
        // Reset existing map state
        clearMap()
        
        val unpackStart = kotlinx.browser.window.performance.now()
        val unpacked = MapPackManager.unpack(packBytes)
        val unpackDuration = kotlinx.browser.window.performance.now() - unpackStart
        Logger.debug(LogCategory.UI, "MapViewer: Unpacked ${unpacked.mapData.pins.size} territories in ${unpackDuration.asDynamic().toFixed(2)}ms")
        
        // Set background image
        val imageStart = kotlinx.browser.window.performance.now()
        val imageBlob = createBlobFromBytes(unpacked.imageBytes)
        val blobUrl = URL.createObjectURL(imageBlob)
        backgroundImage?.src = blobUrl
        val imageDuration = kotlinx.browser.window.performance.now() - imageStart
        Logger.debug(LogCategory.UI, "MapViewer: Background image loaded in ${imageDuration.asDynamic().toFixed(2)}ms")
        
        // Store map data
        mapData = unpacked.mapData
        Logger.debug(LogCategory.UI, "MapViewer: Loaded Map with ${mapData?.pins?.size} territories")
        
        // Create territory icons
        val iconLoopStart = kotlinx.browser.window.performance.now()
        unpacked.mapData.pins.forEach { pinData ->
            addTerritoryIcon(pinData.territory)
        }
        val iconLoopDuration = kotlinx.browser.window.performance.now() - iconLoopStart

        // Build the per-hover border-line index for this map. The
        // helper is pure, so building it is cheap; the resulting
        // [borderRenderer] is what every hover lookup asks.
        borderRenderer = HoverBorderLineRenderer(unpacked.mapData)
        
        val totalDuration = kotlinx.browser.window.performance.now() - totalStart
        Logger.info(LogCategory.UI, "[PERF] [CLIENT] MapViewer.loadMapPack completed in ${totalDuration.asDynamic().toFixed(2)}ms (Unpack: ${unpackDuration.asDynamic().toFixed(2)}ms, Image: ${imageDuration.asDynamic().toFixed(2)}ms, Icons: ${iconLoopDuration.asDynamic().toFixed(2)}ms)")
        isMapLoaded = true

        // Default-center on load: centroid of local-player-owned territories,
        // fall back to centroid of all pins. Don't animate on first load —
        // the player hasn't seen the map yet, instant centering is correct.
        centerOnDefault()
    }
    
    /**
     * Loads a map pack asynchronously in a coroutine scope.
     *
     * @param packBytes The map pack data as ByteArray.
     */
    fun loadMapPackAsync(packBytes: ByteArray)
    {
        MainScope().launch {
            try
            {
                loadMapPack(packBytes)
            }
            catch(e: Exception)
            {
                Logger.error(LogCategory.UI, "MapViewer: Failed to load map pack: ${e.message}")
            }
        }
    }
    
    /**
     * Creates and adds a territory icon to the map.
     *
     * @param territory The territory data for the icon.
     */
    private fun addTerritoryIcon(territory: Territory)
    {
        Logger.debug(LogCategory.UI, "MapViewer: Adding icon for territory '${territory.name}' (type: ${territory.type})")
        
        val defaultIcon = getDefaultIcon(territory.type)
        Logger.debug(LogCategory.UI, "MapViewer: Default icon for ${territory.type} = '$defaultIcon'")
        
        val icon = TerritoryIcon(
            territory = territory,
            iconImage = defaultIcon,
            iconSize = IconSize.MEDIUM
        )
        
        icon.onIconClick = { t ->
            Logger.debug(LogCategory.UI, "MapViewer: Territory clicked callback - '${t.name}'")
            onTerritoryClick?.invoke(t)
        }

        icon.onIconHover = { t, isEnter ->
            if(isEnter)
            {
                Logger.debug(LogCategory.UI, "MapViewer: Territory hover-enter - '${t.name}', drawing border lines")
                showBorderLinesForTerritory(t.name)
            }
            else
            {
                Logger.debug(LogCategory.UI, "MapViewer: Territory hover-leave - '${t.name}', clearing border lines")
                clearBorderLines()
            }
        }

        territoryIcons[territory.name] = icon
        mapCanvas?.add(icon)
        Logger.debug(LogCategory.UI, "MapViewer: Icon added to map for '${territory.name}'")
    }
    
    /**
     * Returns the default icon emoji for a territory type.
     *
     * @param territoryType The type of territory.
     * @return The icon emoji string.
     */
    private fun getDefaultIcon(territoryType: enums.TerritoryType): String
    {
        val icon = when(territoryType)
        {
            enums.TerritoryType.Land -> "🏰"
            enums.TerritoryType.Coastline -> "⚓"
            enums.TerritoryType.Island -> "🏝️"
            enums.TerritoryType.Underwater -> "🪸"
            enums.TerritoryType.Desert -> "🌵"
            enums.TerritoryType.Void -> "👾"
        }
        Logger.debug(LogCategory.UI, "MapViewer: getDefaultIcon(${territoryType}) = '$icon'")
        return icon
    }
    
    /**
     * Creates a Blob from a ByteArray for image display.
     *
     * @param bytes The image data as ByteArray.
     * @return Blob containing the image data.
     */
    private fun createBlobFromBytes(bytes: ByteArray): Blob
    {
        val dynamicBytes = bytes.asDynamic()
        val uint8Array = Uint8Array(dynamicBytes.buffer, dynamicBytes.byteOffset, dynamicBytes.byteLength)
        return Blob(arrayOf(uint8Array), BlobPropertyBag(type = "image/png"))
    }
    
    /**
     * Updates the ownership display for a territory.
     *
     * @param territoryName The name of the territory to update.
     * @param owner The owner's name.
     * @param ownerColor The owner's color in hex format.
     * @param activePlayerName Optional name of the currently active player.
     * @param localPlayerName Optional name of the local player.
     * @param forceAnimate If true, triggers the animation even if the owner has not changed.
     */
    fun updateTerritoryOwnership(
        territoryName: String, 
        owner: String, 
        ownerColor: String,
        activePlayerName: String? = null,
        localPlayerName: String? = null,
        forceAnimate: Boolean = false
    )
    {
        val callStart = kotlinx.browser.window.performance.now()
        val icon = territoryIcons[territoryName]
        if(icon != null)
        {
            val resolvedLocalPlayer = localPlayerName ?: globals.World.localPlayer.name
            val world = globals.World.worldData
            val ownerPresentation = resolveTerritoryOwnerPresentation(
                ownerName = owner,
                localPlayerName = resolvedLocalPlayer,
                activePlayerNames = world.activePlayers.map { player -> player.name },
                npcNames = world.npc.map { npc -> npc.name }
            )
            val emojiIcon = ownerPresentation.icon ?: getDefaultIcon(icon.territory.type)

            Logger.debug(
                LogCategory.UI,
                "MapViewer.updateTerritoryOwnership: '$territoryName' - Owner: '$owner', " +
                    "Local: '$resolvedLocalPlayer', OwnerKind: ${ownerPresentation.ownerKind}, " +
                    "PlayerIndex: ${ownerPresentation.playerIndex}, Icon: $emojiIcon"
            )
            if(ownerPresentation.ownerKind == TerritoryOwnerKind.UNKNOWN)
            {
                Logger.warn(
                    LogCategory.UI,
                    "MapViewer.updateTerritoryOwnership: UNKNOWN owner '$owner' for '$territoryName', using default icon"
                )
            }

            // Keep the territory record in sync so description popups see the latest ruler.
            icon.territory.ruler = owner
            icon.updateOwnership(owner, ownerColor, emojiIcon, activePlayerName, localPlayerName, forceAnimate)
        }
        else
        {
            // Silently ignore - territory may not be added to map yet
            Logger.warn(LogCategory.UI, "MapViewer: Territory '$territoryName' not found in icon map (Size: ${territoryIcons.size})")
        }

        val callDuration = kotlinx.browser.window.performance.now() - callStart
        totalUpdateOwnershipDuration += callDuration
        updateOwnershipCallCount++
        
        if(updateOwnershipCallCount >= 100)
        {
            val avg = totalUpdateOwnershipDuration / updateOwnershipCallCount
            Logger.info(LogCategory.UI, "[PERF] [CLIENT] MapViewer.updateTerritoryOwnership (Avg of last 100): ${avg.asDynamic().toFixed(4)}ms")
            updateOwnershipCallCount = 0
            totalUpdateOwnershipDuration = 0.0
        }
    }
    
    /**
     * Updates the state display for a territory.
     *
     * @param territoryName The name of the territory to update.
     * @param state The new territory state.
     */
    fun updateTerritoryState(territoryName: String, state: TerritoryState)
    {
        Logger.debug(LogCategory.UI, "MapViewer: Updating state for '$territoryName' - State: $state")
        val icon = territoryIcons[territoryName]
        if(icon != null)
        {
            icon.updateState(state)
            Logger.debug(LogCategory.UI, "MapViewer: State updated successfully")
        }
        else
        {
            // Silently ignore - territory may not be added to map yet
            Logger.debug(LogCategory.UI, "MapViewer: Territory '$territoryName' not found in icon map")
        }
    }
    
    /**
     * Retrieves a territory icon by name.
     *
     * @param territoryName The name of the territory.
     * @return The TerritoryIcon or null if not found.
     */
    fun getTerritoryIcon(territoryName: String): TerritoryIcon?
    {
        return territoryIcons[territoryName]
    }
    
    /**
     * Rebuilds the SVG `<line>` children of [svgLineGroup] so the
     * hovered territory's direct neighbors are visually connected
     * to it.
     *
     * The geometry comes from [HoverBorderLineRenderer]; the SVG
     * work stays here so the renderer can stay pure and unit-
     * testable. Each line is written as percent-attribute values
     * (0..100) so it tracks the background image as the container
     * resizes. Stroke is fixed at gold for v1 (no owner-aware
     * coloring yet — see plan).
     *
     * Safe to call when [borderRenderer] or [svgLineGroup] is
     * null: the function becomes a no-op so it can be called from
     * the hover callback without null-guarding at the call site.
     *
     * @param territoryName The territory under the cursor.
     */
    private fun showBorderLinesForTerritory(territoryName: String)
    {
        val renderer = borderRenderer ?: return
        val group = svgLineGroup ?: return
        val specs = renderer.linesForHoveredTerritory(territoryName)
        group.removeAll()
        for(spec in specs)
        {
            group.add(
                customTag("line") {
                    setAttribute("x1", "${spec.x1}%")
                    setAttribute("y1", "${spec.y1}%")
                    setAttribute("x2", "${spec.x2}%")
                    setAttribute("y2", "${spec.y2}%")
                    setAttribute("stroke", "#FFD700")
                    setAttribute("stroke-width", "2")
                    setAttribute("data-connection", spec.connectionKey)
                }
            )
        }
    }

    /**
     * Removes every SVG `<line>` child from [svgLineGroup] so the
     * map returns to its idle (line-free) state.
     *
     * Called from the hover-leave callback. Safe to call when
     * [svgLineGroup] is null.
     */
    private fun clearBorderLines()
    {
        svgLineGroup?.removeAll()
    }

    /**
     * Clears all territories and resets the map.
     */
    fun clearMap()
    {
        clearBorderLines()
        borderRenderer = null
        territoryIcons.values.forEach { mapCanvas?.remove(it) }
        territoryIcons.clear()
        backgroundImage?.src = ""
        mapData = null
    }

    // ========================================================================
    // Pan + zoom + center — public API and pointer event handlers
    // ========================================================================
    //
    // Architecture:
    //   - mapCanvas owns `transform: translate3d(tx, ty, 0) scale(z)`
    //   - panX/panY/zoom are the source of truth
    //   - applyTransform() is the ONLY function that writes to mapCanvas.style.transform
    //   - clampToBounds() is called after every pan/zoom mutation
    //
    // Mobile gating: pointer handlers (wirePanHandlers / wirePinchHandlers)
    // bail early when !isMobile so desktop keeps its hover-and-click
    // interaction on TerritoryIcon children.

    /**
     * Centers the map on the named territory. If [animated] is true,
     * the move uses a ~400ms ease-out cubic; otherwise it snaps
     * instantly. Falls back silently if the territory isn't loaded.
     */
    fun centerOnTerritory(name: String, animated: Boolean = true)
    {
        val tile = globals.World.worldData.mapTiles
            .find { it.name.equals(name, ignoreCase = true) }
            ?: mapData?.pins?.map { it.territory }
                ?.find { it.name.equals(name, ignoreCase = true) }
            ?: return
        centerOnPoint(tile.xPos, tile.yPos, animated)
    }

    /**
     * Centers the map on a percent point (x%, y%) of the canvas. The
     * point's percent position on the canvas lands at the center of
     * the visible viewport. Honors current zoom level.
     */
    fun centerOnPoint(xPercent: Double, yPercent: Double, animated: Boolean = true)
    {
        val canvasEl = mapCanvas?.getElement() as? org.w3c.dom.HTMLElement ?: return
        val containerEl = this.getElement() as? org.w3c.dom.HTMLElement ?: return
        val containerRect = containerEl.getBoundingClientRect()
        // The canvas fills 100% of the container; we use the container's
        // dimensions for both, scaled by the current zoom.
        val cw = containerRect.width
        val ch = containerRect.height
        val targetTx = (cw / 2.0) - (cw / zoom) * (xPercent / 100.0)
        val targetTy = (ch / 2.0) - (ch / zoom) * (yPercent / 100.0)
        val (finalTx, finalTy) = clampToBounds(targetTx, targetTy, zoom)
        if(!animated)
        {
            panX = finalTx
            panY = finalTy
            applyTransform()
            return
        }
        animateTo(finalTx, finalTy)
    }

    /**
     * Default-center target: centroid of local-player-owned territories,
     * fall back to centroid of all pins. Called from [loadMapPack].
     */
    fun centerOnDefault()
    {
        val owned = globals.World.worldData.mapTiles
            .filter { it.ruler.trim().equals(globals.World.localPlayer.name.trim(), ignoreCase = true) }
        val tiles: List<Territory> = if(owned.isNotEmpty())
        {
            owned
        }
        else
        {
            globals.World.worldData.mapTiles.takeIf { it.isNotEmpty() }
                ?: mapData?.pins?.map { it.territory }.orEmpty()
        }
        if(tiles.isEmpty()) return
        val cx = tiles.map { it.xPos }.average()
        val cy = tiles.map { it.yPos }.average()
        centerOnPoint(cx, cy, animated = false)
    }

    /**
     * Programmatically pans by (dx, dy) CSS pixels. Animates over ~400ms
     * if [animated] is true. Clamps to bounds.
     */
    fun panBy(dx: Double, dy: Double, animated: Boolean = false)
    {
        val (finalTx, finalTy) = clampToBounds(panX + dx, panY + dy, zoom)
        if(!animated)
        {
            panX = finalTx
            panY = finalTy
            applyTransform()
            return
        }
        animateTo(finalTx, finalTy)
    }

    /**
     * Programmatically zooms by [factor] (e.g. 1.1 to zoom in 10%, 0.9
     * to zoom out 10%) with the focal point anchored at ([focalX],
     * [focalY]) in container-local pixel coordinates. Defaults to the
     * container's center.
     */
    fun zoomBy(factor: Double, focalX: Double? = null, focalY: Double? = null)
    {
        val containerEl = this.getElement() as? org.w3c.dom.HTMLElement ?: return
        val rect = containerEl.getBoundingClientRect()
        val fx = focalX ?: rect.width / 2.0
        val fy = focalY ?: rect.height / 2.0
        val newZoom = (zoom * factor).coerceIn(minZoom, maxZoom)
        // Focal-point preservation: pan so the canvas point under (fx, fy)
        // stays under (fx, fy) after zoom.
        val panAdjustX = fx * (1.0 - zoom / newZoom)
        val panAdjustY = fy * (1.0 - zoom / newZoom)
        val newTx = panX + panAdjustX
        val newTy = panY + panAdjustY
        zoom = newZoom
        val (finalTx, finalTy) = clampToBounds(newTx, newTy, zoom)
        panX = finalTx
        panY = finalTy
        applyTransform()
    }

    /**
     * Sets zoom to [z] (clamped to [minZoom]..[maxZoom]) and clamps pan.
     * Optionally animate the zoom change over ~400ms via requestAnimationFrame.
     */
    fun zoomTo(z: Double, animated: Boolean = false)
    {
        val target = z.coerceIn(minZoom, maxZoom)
        if(!animated)
        {
            val factor = target / zoom
            zoomBy(factor)
            return
        }
        // For zoom animation we step the zoom by factor toward target on each frame.
        val startZoom = zoom
        animateZoomTo(startZoom, target)
    }

    /**
     * Single-source-of-truth for the canvas transform. Every pan/zoom
     * mutation goes through this so the DOM stays consistent with the
     * state fields.
     */
    private fun applyTransform()
    {
        mapCanvas?.setStyle("transform",
            "translate3d(${panX}px, ${panY}px, 0) scale(${zoom})")
    }

    /**
     * Clamps [tx]/[ty] so the visible canvas rect stays inside the
     * container. When canvas >= container (zoomed in), pan is clamped
     * so no empty space shows. When canvas < container (zoomed out so
     * the whole map fits), center it.
     */
    private fun clampToBounds(tx: Double, ty: Double, z: Double): Pair<Double, Double>
    {
        val containerEl = this.getElement() as? org.w3c.dom.HTMLElement ?: return tx to ty
        val rect = containerEl.getBoundingClientRect()
        val cw = rect.width
        val ch = rect.height
        val iw = cw / z
        val ih = ch / z
        return if(iw >= cw)
        {
            // Image fills or overflows container — clamp so no empty space.
            val clampedTx = tx.coerceIn(cw - iw, 0.0)
            val clampedTy = ty.coerceIn(ch - ih, 0.0)
            clampedTx to clampedTy
        }
        else
        {
            // Image fits inside container with margin — center it.
            (cw - iw) / 2.0 to (ch - ih) / 2.0
        }
    }

    /**
     * Animates pan to (targetTx, targetTy) over ~400ms using
     * requestAnimationFrame and an ease-out cubic curve. Calls
     * applyTransform on each tick.
     */
    private fun animateTo(targetTx: Double, targetTy: Double)
    {
        val startX = panX
        val startY = panY
        val t0 = kotlinx.browser.window.performance.now()
        fun step()
        {
            val now = kotlinx.browser.window.performance.now()
            val t = ((now - t0) / 400.0).coerceIn(0.0, 1.0)
            val ease = 1.0 - (1.0 - t).pow(3)
            panX = startX + (targetTx - startX) * ease
            panY = startY + (targetTy - startY) * ease
            // Re-clamp each frame in case external state changed.
            val (clampedX, clampedY) = clampToBounds(panX, panY, zoom)
            panX = clampedX
            panY = clampedY
            applyTransform()
            if(t < 1.0) kotlinx.browser.window.requestAnimationFrame { step() }
        }
        kotlinx.browser.window.requestAnimationFrame { step() }
    }

    /**
     * Animates zoom from [startZ] to [targetZ] over ~400ms. Each frame
     * uses zoomBy(factor) so focal-point math is consistent.
     */
    private fun animateZoomTo(startZ: Double, targetZ: Double)
    {
        val t0 = kotlinx.browser.window.performance.now()
        fun step()
        {
            val now = kotlinx.browser.window.performance.now()
            val t = ((now - t0) / 400.0).coerceIn(0.0, 1.0)
            val ease = 1.0 - (1.0 - t).pow(3)
            val interpolated = startZ + (targetZ - startZ) * ease
            zoom = interpolated.coerceIn(minZoom, maxZoom)
            val (clampedX, clampedY) = clampToBounds(panX, panY, zoom)
            panX = clampedX
            panY = clampedY
            applyTransform()
            if(t < 1.0) kotlinx.browser.window.requestAnimationFrame { step() }
        }
        kotlinx.browser.window.requestAnimationFrame { step() }
    }

    /**
     * Single-pointer drag-pan handler. Mobile-only — bails on desktop
     * so existing TerritoryIcon hover/click interaction is preserved.
     *
     * Tracks one pointer's clientX/clientY deltas, applies them to
     * panX/panY, and on pointerup applies a velocity-based fling
     * (80ms worth of decay). Always clamps via clampToBounds.
     */
    private fun wirePanHandlers()
    {
        if(!isMobile) return
        val canvasEl = mapCanvas?.getElement() as? org.w3c.dom.HTMLElement ?: return
        // Suppress browser default scroll/zoom on touch inside the canvas.
        canvasEl.setAttribute("style",
            (canvasEl.getAttribute("style") ?: "") + "; touch-action: none; cursor: grab;")
        var pointerId: Int? = null
        var lastX = 0.0
        var lastY = 0.0
        var lastMoveTime = 0.0
        var velocityX = 0.0
        var velocityY = 0.0

        canvasEl.addEventListener("pointerdown", { e ->
            val pe = e.asDynamic()
            // Only single-pointer drags drive pan; pinch owns 2-pointer.
            // (Pinch handler will activate when a second pointer arrives.)
            pointerId = (pe.pointerId as Int)
            lastX = (pe.clientX as Double)
            lastY = (pe.clientY as Double)
            lastMoveTime = kotlinx.browser.window.performance.now()
            velocityX = 0.0
            velocityY = 0.0
            canvasEl.setAttribute("style",
                (canvasEl.getAttribute("style") ?: "").replace("cursor: grab", "cursor: grabbing"))
        })

        canvasEl.addEventListener("pointermove", { e ->
            val pid = pointerId ?: return@addEventListener
            val pe = e.asDynamic()
            if((pe.pointerId as Int) != pid) return@addEventListener
            val now = kotlinx.browser.window.performance.now()
            val dt = (now - lastMoveTime).coerceAtLeast(1.0)
            val cx = (pe.clientX as Double)
            val cy = (pe.clientY as Double)
            val dx = cx - lastX
            val dy = cy - lastY
            velocityX = dx / dt
            velocityY = dy / dt
            lastX = cx
            lastY = cy
            lastMoveTime = now
            panX += dx
            panY += dy
            val (clampedX, clampedY) = clampToBounds(panX, panY, zoom)
            panX = clampedX
            panY = clampedY
            applyTransform()
        })

        val release: (org.w3c.dom.events.Event) -> Unit = handler@{ e ->
            val pid = pointerId ?: return@handler
            val pe = e.asDynamic()
            // If pointerId is missing or pe has no pointerId (e.g. raw Event),
            // skip. Use a manual cast to avoid Kotlin compiler complaint
            // about dynamic `as Int` inside try/catch.
            val incomingId: Int? = pe.pointerId as? Int
            if(incomingId != null && incomingId != pid) return@handler
            pointerId = null
            // Velocity fling — 80ms worth of decay in the direction of the last move.
            panX += velocityX * 80.0
            panY += velocityY * 80.0
            val (clampedX, clampedY) = clampToBounds(panX, panY, zoom)
            panX = clampedX
            panY = clampedY
            applyTransform()
            canvasEl.setAttribute("style",
                (canvasEl.getAttribute("style") ?: "").replace("cursor: grabbing", "cursor: grab"))
        }
        canvasEl.addEventListener("pointerup", release)
        canvasEl.addEventListener("pointercancel", release)
    }

    /**
     * Two-pointer pinch-zoom handler. Mobile-only.
     *
     * Tracks the distance and midpoint of two simultaneous pointers;
     * when both are present, computes the scale factor and applies it
     * with focal-point preservation. Each pointerdown/pointermove/
     * pointerup is shared with wirePanHandlers via the activePointers
     * map so the two never fight over the same pointer.
     */
    private fun wirePinchHandlers()
    {
        if(!isMobile) return
        val canvasEl = mapCanvas?.getElement() as? org.w3c.dom.HTMLElement ?: return
        val activePointers = mutableMapOf<Int, Pair<Double, Double>>()
        var pinchStartDistance: Double? = null
        var pinchStartZoom: Double = 1.0
        var pinchFocalX: Double = 0.0
        var pinchFocalY: Double = 0.0
        var pinchPanX: Double = 0.0
        var pinchPanY: Double = 0.0

        canvasEl.addEventListener("pointerdown", { e ->
            val pe = e.asDynamic()
            val id = pe.pointerId as Int
            activePointers[id] = (pe.clientX as Double) to (pe.clientY as Double)
            if(activePointers.size == 2)
            {
                val p = activePointers.values.toList()
                pinchStartDistance = sqrt((p[0].first - p[1].first).pow(2) + (p[0].second - p[1].second).pow(2))
                pinchStartZoom = zoom
                pinchFocalX = (p[0].first + p[1].first) / 2.0
                pinchFocalY = (p[0].second + p[1].second) / 2.0
                pinchPanX = panX
                pinchPanY = panY
            }
        })

        canvasEl.addEventListener("pointermove", { e ->
            val pe = e.asDynamic()
            val id = pe.pointerId as Int
            if(id !in activePointers) return@addEventListener
            activePointers[id] = (pe.clientX as Double) to (pe.clientY as Double)
            if(activePointers.size == 2)
            {
                val p = activePointers.values.toList()
                val newDistance = sqrt((p[0].first - p[1].first).pow(2) + (p[0].second - p[1].second).pow(2))
                val start = pinchStartDistance ?: 1.0
                val factor = newDistance / start
                val newZoom = (pinchStartZoom * factor).coerceIn(minZoom, maxZoom)
                val containerEl = this.getElement() as? org.w3c.dom.HTMLElement ?: return@addEventListener
                val rect = containerEl.getBoundingClientRect()
                val focalOffsetX = pinchFocalX - rect.left
                val focalOffsetY = pinchFocalY - rect.top
                val panAdjustX = focalOffsetX * (1.0 - pinchStartZoom / newZoom)
                val panAdjustY = focalOffsetY * (1.0 - pinchStartZoom / newZoom)
                zoom = newZoom
                val newPanX = pinchPanX + panAdjustX
                val newPanY = pinchPanY + panAdjustY
                val (clampedX, clampedY) = clampToBounds(newPanX, newPanY, zoom)
                panX = clampedX
                panY = clampedY
                applyTransform()
            }
        })

        canvasEl.addEventListener("pointerup", { e ->
            val pe = e.asDynamic()
            val id = pe.pointerId as Int
            activePointers.remove(id)
            if(activePointers.size < 2) pinchStartDistance = null
        })

        canvasEl.addEventListener("pointercancel", { e ->
            val pe = e.asDynamic()
            val id = pe.pointerId as Int
            activePointers.remove(id)
            if(activePointers.size < 2) pinchStartDistance = null
        })
    }
}

/**
 * Loads a map pack file and returns its contents as ByteArray.
 *
 * @param file The File object to read.
 * @return ByteArray containing the file data.
 */
suspend fun loadMapPackFile(file: File): ByteArray = suspendCancellableCoroutine { cont ->
    val reader = FileReader()
    reader.onload = {
        val arrayBuffer = reader.result as ArrayBuffer
        val uint8Array = Uint8Array(arrayBuffer)
        val bytes = ByteArray(uint8Array.length)
        for(i in bytes.indices) bytes[i] = uint8Array.asDynamic()[i] as Byte
        cont.resume(bytes)
    }
    reader.onerror = { cont.cancel() }
    reader.readAsArrayBuffer(file)
}

/**
 * Fetches a map pack from a URL and returns its contents as ByteArray.
 *
 * @param url The URL to fetch from.
 * @return ByteArray containing the fetched data.
 */
suspend fun fetchMapPackFromUrl(url: String): ByteArray
{
    val response = kotlinx.browser.window.fetch(url).await()
    val arrayBuffer = response.arrayBuffer().await()
    val uint8Array = Uint8Array(arrayBuffer)
    val bytes = ByteArray(uint8Array.length)
    for(i in bytes.indices) bytes[i] = uint8Array.asDynamic()[i] as Byte
    return bytes
}
